// SignatureImageNode — TipTap node for excerptus.signatureImage.
// Renders a small, alignable inline signature (no caption / credit chrome).
// Size and alignment are set in the Signature block editor, not in the canvas.
import { Node, mergeAttributes } from "@tiptap/core";
import { ReactNodeViewRenderer, NodeViewWrapper, type NodeViewProps } from "@tiptap/react";

type Size = "small" | "medium" | "large";
type Align = "left" | "center";

const SIZE_CLASS: Record<Size, string> = {
  small: "h-12",   // ~48px
  medium: "h-16",  // ~64–72px
  large: "h-24",   // ~96px
};

const ALIGN_CONTAINER_CLASS: Record<Align, string> = {
  left: "justify-start",
  center: "justify-center",
};

const SignatureImageView = ({ node }: NodeViewProps) => {
  const src = (node.attrs.src as string) ?? "";
  const alt = (node.attrs.alt as string) ?? "";
  const size = ((node.attrs.size as Size) ?? "medium");
  const align = ((node.attrs.align as Align) ?? "left");

  if (!src) return null;
  return (
    <NodeViewWrapper
      as="div"
      className={`not-prose -mt-2 mb-1 flex w-full ${ALIGN_CONTAINER_CLASS[align]}`}
      data-signature-image
    >
      <img
        src={src}
        alt={alt}
        className={`${SIZE_CLASS[size]} my-0 w-auto object-contain`}
        draggable={false}
      />
    </NodeViewWrapper>
  );
};

export const SignatureImageNode = Node.create({
  name: "excerptus.signatureImage",
  group: "block",
  atom: true,
  selectable: true,
  draggable: false,
  addAttributes() {
    return {
      src: { default: "" },
      alt: { default: "" },
      size: { default: "medium" },
      align: { default: "left" },
    };
  },
  parseHTML() {
    return [{ tag: "div[data-signature-image]" }];
  },
  renderHTML({ HTMLAttributes }) {
    const size = (HTMLAttributes.size as Size) ?? "medium";
    const align = (HTMLAttributes.align as Align) ?? "left";
    return [
      "div",
      mergeAttributes(
        { class: `signature-image signature-image--${size} signature-image--${align}` },
        { "data-signature-image": "true", "data-size": size, "data-align": align },
      ),
      [
        "img",
        {
          src: HTMLAttributes.src ?? "",
          alt: HTMLAttributes.alt ?? "",
          class: `${SIZE_CLASS[size]} w-auto object-contain`,
        },
      ],
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(SignatureImageView);
  },
});
